import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  SpheraHeader,
  SpheraNotificationGroup,
  SpheraNotification,
  SpheraDropdownEx,
  SpheraConfirmation,
} from '@sphera-cloud/component-ui-reactjs';
import { Grid, GridSelectionChangeEvent, GridSortChangeEvent, ISortDescriptor, ISpheraGridMetaData, SubTab, Tab } from 'lca-common-ui-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { TruncateText } from 'src/components/TruncateText';
import { useSpinnerContext } from 'src/context/SpinnerContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { CreateJobInput, Job, JobStatus } from 'src/store/slices/jobs/types';
import {
  createJob,
  deleteJob,
  fetchJobs,
  isLoadingSelector,
  jobsSelector,
  clearJobs,
} from 'src/store/slices/jobs';
import CreateJobModal from '../../components/CreateJob';
import { fetchJobSpec, jobSpecSelector } from 'src/store/slices/jobSpec';
import { fetchFileSpec } from 'src/store/slices/fileSpec';
import { INotificationObj } from '../Files/types';
import { setMenu } from 'src/store/slices/menus';
import config from 'src/components/Navbar/config';
import { JobSpec } from 'src/store/slices/jobSpec/types';
import { JOBS_RELOAD_TIME } from 'src/utilities/constants';
import './styles.scss';
import { useUserSettings } from 'src/hooks/useUserSettings';
import { ITEMS_PER_PAGE, PAGE_OPTIONS } from 'src/helpers/Constants';

const initialPageOptions = {
  skip: 0,
  take: ITEMS_PER_PAGE,
};

const initialSort: Array<ISortDescriptor> = [{ field: '', dir: 'asc' }];
const defaultNotification = { isOpen: false, type: '', text: '' };
const filterOperators = {
  text: [{ text: 'grid.filterStartsWithOperator', operator: 'startswith' }],
};
const DataItemKey = 'jobId';

export const JobsPage: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [copyFromJob, setCopyFromJob] = useState<Job | null>(null);
  const [showCreationModal, setShowCreationModal] = useState<boolean>(false);
  const [notification, setNotification] = useState(defaultNotification);
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>();
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string>('');
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false);
  const [bulkDeleteConfirmation, setBulkDeleteConfirmation] = useState<boolean>(false);
  const { getFormattedDateTime, getFormattedExpirationDateTime } = useUserSettings();
  const [selectedJobs, setSelectedJobs] = useState<Record<string, Job>>({});

  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);
  const { setShowSpinner } = useSpinnerContext();
  const [pageOptions, setPageOptions] = useState<{ skip: number; take: number }>(
    initialPageOptions,
  );
  const [sort, setSort] = useState(initialSort);
  const { hash } = useLocation();

  const { items: jobs, totalCount } = useAppSelector(jobsSelector);
  const isLoading = useAppSelector(isLoadingSelector);
  const initialJobSpec = useAppSelector(jobSpecSelector);
  const jobSpec = initialJobSpec.map((item: JobSpec) => {
    return { label: item.name, value: item.id };
  });
  const typeOptions = [{ label: '', value: '' }, ...jobSpec];

  const changeTypeFilter = (e: any) => {
    setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    setTypeFilter(e?.value.value);
  };

  const typeFilterCell = () => (
    <SpheraDropdownEx
      options={typeOptions}
      type="basic"
      onChange={changeTypeFilter}
      value={typeFilter}
      width={130}
    />
  );

  useEffect(() => {
    setShowSpinner(isLoading);
  }, [isLoading]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      dispatch(fetchJobSpec());
      dispatch(fetchFileSpec());
    }, 0);
    return () => clearTimeout(delayDebounceFn);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setMenu(config.menus));
      dispatch(clearJobs(undefined));
    };
  }, []);

  const generateNotificationObj = (type: string, message: string): INotificationObj => {
    return {
      isOpen: true,
      type,
      text: message,
    };
  };
  const showNotification = (type: string, message: string) => {
    setNotification(generateNotificationObj(type, message));
  };

  const hideNotification = () => {
    setTimeout(() => {
      setNotification(defaultNotification);
    }, 5000);
  };

  const handleSortChange = (e: GridSortChangeEvent) => {
    if (pageOptions.skip !== initialPageOptions.skip) {
      setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    }
    setSort(e.sort as ISortDescriptor[]);
  };

  const jobOptions = useMemo(
    () => [
      {
        status: [JobStatus.Succeeded, JobStatus.SucceededWithIssues],
        title: 'Successful Jobs',
      },
      {
        status: JobStatus.Running,
        title: 'Running Jobs',
      },
      {
        status: JobStatus.NotStarted,
        title: 'Pending Jobs',
      },
      {
        status: JobStatus.Failed,
        title: 'Failed Jobs',
      },
    ],
    [],
  );

  const pageChange = (event: { page: { skip: number; take: number } }) => {
    const { skip, take } = event.page;
    setPageOptions({ skip, take });
  };

  useEffect(() => {
    let jobsOptions: any = {};
    let delayDebounceFn: ReturnType<typeof setTimeout>;

    const { skip, take } = pageOptions;
    const page = skip / take + 1;
    const hashIndex = hash.split('-')[1] || 0;
    const { status } = jobOptions[+hashIndex];
    setSelectedOptionIndex(+hashIndex);
    let sortFilter = sort[0]?.field;
    if (sortFilter) {
      sortFilter = `${sortFilter} ${sort[0].dir}`;
    }

    if (filter || typeFilter || sortFilter) {
      // @ts-ignore
      const nameFilter = filter?.filters.find((item) => item.field === 'name')?.value;
      const jobsFilters = { status, page, take, type: typeFilter, name: nameFilter, sort: sortFilter };

      delayDebounceFn = setTimeout(() => {
        dispatch(fetchJobs(jobsFilters));
        jobsOptions = jobsFilters;
      }, 500);
    } else {
      dispatch(fetchJobs({ status, page, take, sort: sortFilter }));
      jobsOptions = { status, page, take, sort: sortFilter };
    }

    const interval = setInterval(() => {
      dispatch(fetchJobs({ ...jobsOptions, isRefresh: true }));
    }, JOBS_RELOAD_TIME);

    return () => {
      clearInterval(interval);
      clearTimeout(delayDebounceFn);
    };

  }, [hash, pageOptions, filter, typeFilter, sort]);

  const clearNameFilter = () => {
    const clearButton = document.querySelector('.icon-clear') as HTMLElement;
    if (clearButton) {
      clearButton.click();
    }
  };

  useEffect(() => {
    setFilter(undefined);
    setTypeFilter('');
    clearNameFilter();
    setPageOptions(initialPageOptions);
    setSort(initialSort);
  }, [hash]);

  const handleSelect = useCallback(({ selected }: { selected: number }) => {
    navigate(`/jobs#tab-${selected}`);
  }, []);

  const handleCreateJob = (input: CreateJobInput) => {
    dispatch(createJob(input));
    setShowCreationModal(false);
  };

  const renderName = ({ dataItem }: { dataItem: Job }) => {
    return <td>
      <Link title="Name" to={`/jobs/${dataItem.jobId}`} data-testid="commit-link">
        <TruncateText text={dataItem.name ?? ''} />
      </Link>
    </td>;
  };

  const renderDate = ({ dataItem }: { dataItem: Job }) => {
    const date = getFormattedDateTime(dataItem.creationDateTime);
    return <td>
      <TruncateText text={date ?? ''} />
    </td>;
  };

  const renderExpDate = ({ dataItem }: { dataItem: Job }) => {
    const date = getFormattedExpirationDateTime(dataItem.expirationDate);
    return <td>
      <TruncateText text={date ?? ''} />
    </td>;
  };

  const renderProgress = ({ dataItem }: { dataItem: Job }) => {
    const progress = `${dataItem.percentComplete}%`;
    return <td>  <TruncateText text={progress ?? ''} /></td>;
  };

  const renderType = ({ dataItem }: { dataItem: Job }) => {
    const itemSpecInfo = initialJobSpec.find(item => dataItem.type === item.id);
    return <td>
      <Link title="Job name" to={`/jobs/${dataItem.jobId}`} data-testid="commit-link"      >
        <TruncateText text={itemSpecInfo?.name ?? ''} tooltipText={itemSpecInfo?.description} />
      </Link >
    </td>;
  };

  const filterChange = (e: any) => {
    setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    setFilter(e?.filter);
  };

  const openConfirmation = (job: Job) => {
    setDeleteConfirmation(true);
    setSelectedId(job.jobId ?? '');
  };

  const closeConfirmation = () => {
    setDeleteConfirmation(false);
    setSelectedId('');
    setCopyFromJob(null);
  };

  const removeJob = async (id: string) => {
    await dispatch(deleteJob(id));
    closeConfirmation();
    await dispatch(
      fetchJobs({ status: jobOptions[selectedOptionIndex].status, page: 1, take: 10 }),
    );
  };

  const removeSelectedJobs = async () => {
    const errors: string[] = [];

    const jobIds = Object.keys(selectedJobs ?? {});
    for (const jobId of jobIds) {
      const response = await dispatch(deleteJob(jobId));
      if (response.type.includes('rejected')) {
        const errorMessage = (response.payload as any)['message'] ?? 'Something went wrong!';
        errors.push(errorMessage);
      } else {
        showNotification('success', 'The jobs were successfully deleted.');
      }
    }
    if (errors.length) {
      showNotification('error', errors.join('\n'));
    } else {
      showNotification('success', 'The jobs were successfully deleted.');
    }

    hideNotification();
    setBulkDeleteConfirmation(false);
    setSelectedJobs({});
    setPageOptions({ ...pageOptions });
  };

  const metadata: ISpheraGridMetaData[] = [
    { field: 'name', title: 'Name', filterable: true, filterCell: 'text', type: 'component', object: '_name', sortable: true },
    { field: 'date', title: 'Creation Date', width: '260px', type: 'component', object: '_date', filterable: true, filterCell: 'custom', customFilter: <></>, sortable: true },
    { field: 'progress', title: 'Progress', type: 'component', object: '_progress', width: '100px', filterable: true, filterCell: 'custom', customFilter: <></>, sortable: true },
    {
      field: 'type',
      title: 'Type',
      filterable: true,
      filterCell: 'custom',
      customFilter: typeFilterCell(),
      type: 'component',
      object: '_type',
      sortable: true,
    },
    {
      field: 'expirationDate',
      title: 'Expires On',
      width: '260px',
      filterable: true,
      filterCell: 'custom',
      type: 'component',
      object: '_expirationDate',
      sortable: true,
    },
  ];

  const tableData = useMemo(() => {
    return jobs?.map(item => {
      const row = {
        ...item,
        selected: !!selectedJobs[item.jobId as string],
      };
      return {
        ...row,
        _name: (renderName({ dataItem: row }) as any)?.props?.children,
        _date: (renderDate({ dataItem: row }) as any)?.props?.children,
        _progress: (renderProgress({ dataItem: row }) as any)?.props?.children,
        _type: (renderType({ dataItem: row }) as any)?.props?.children,
        _expirationDate: (renderExpDate({ dataItem: row }) as any)?.props?.children,
      };
    });
  }, [jobs, selectedJobs, initialJobSpec, getFormattedDateTime, getFormattedExpirationDateTime]);

  const actions = [
    {
      text: 'Job details',
      onClick: (job: Job) => {
        navigate(`/jobs/${job.jobId}`);
      },
      icon: 'icon-ui-general-file',
    },
    {
      text: 'Copy Job',
      onClick: (job: Job) => {
        setCopyFromJob(job);
        setShowCreationModal(true);
      },
      icon: 'icon-ui-general-copy',
    },

    {
      text: 'Delete Job',
      onClick: (job: Job) => {
        openConfirmation(job);
      },
      icon: 'icon-ui-general-delete',
    },
  ];

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedJobs = { ...selectedJobs };
      if (event.dataItem.selected) {
        newSelectedJobs[event.dataItem[DataItemKey]] = event.dataItem;
      } else {
        delete newSelectedJobs[event.dataItem[DataItemKey]];
      }
      setSelectedJobs(newSelectedJobs);
    },
    [selectedJobs],
  );

  const selectedIds = useMemo<string[]>(() => {
    return Object.keys(selectedJobs ?? {}).map(key => selectedJobs[key][DataItemKey] as string);
  }, [selectedJobs]);

  const renderSelectedJobs = () => {
    const items = Object.values(selectedJobs ?? {});
    if (!items.length) return;
    return (
      <ul>
        {items.map(item => {
          return (<li key={item.jobId}><b>{item.name}</b></li>);
        })}
      </ul>
    );
  };

  return (
    <div className="job-page inner-content">
      <SpheraHeader
        text="Jobs"
        navigate={navigate}
        breadcrumbData={[
          { id: 1, text: 'Sphera Cloud LCA', link: '/' },
          {
            id: 2,
            text: 'Jobs',
            link: '/jobs',
          },
        ]}
        button={[{
          type: selectedIds?.length ? 'secondary' : 'disabled',
          icon: 'icon-ui-general-delete',
          onClick: () => {
            setBulkDeleteConfirmation(true);
          },
          tooltip: 'Delete jobs',
          position: 'top',
        },
        {
          type: 'primary',
          icon: '',
          text: 'Add Job',
          onClick: () => {
            setShowCreationModal(true);
          },
          tooltip: '',
          position: '',
        },
        ]}
      />

      <Tab onSelect={handleSelect} selected={selectedOptionIndex}>
        {jobOptions.map(({ title }) => (
          <SubTab key={title} title={title} />
        ))}
      </Tab>

      <Grid
        serverSideData
        pageable
        filterable
        data={tableData}
        filter={filter}
        sort={sort}
        filterOperators={filterOperators}
        pageOptions={PAGE_OPTIONS}
        onSortChange={handleSortChange}
        maxAction={4}
        onPageChange={pageChange}
        //onRowClick={({ dataItem }: { dataItem: Job }) => navigate(`/jobs/${dataItem.jobId}`)}
        onFilterChange={filterChange}
        metadata={metadata}
        total={totalCount}
        actions={actions}
        {...pageOptions}
        selectable={{
          enabled: true,
          drag: false,
          cell: false,
          mode: 'multiple',
        }}
        selectedField="selected"
        headerCellRender={(td: any, { field }: { field: string }) => {
          if (field === 'selected') return null;
          return td;
        }}
        showSelectionCheckbox
        onSelectionChange={onSelectionChange}
        dataItemKey={DataItemKey}
      />
      {showCreationModal && (
        <CreateJobModal
          showNotification={showNotification}
          hideNotification={hideNotification}
          onClose={() => {
            setShowCreationModal(false);
            setCopyFromJob(null);
          }}
          onCreate={handleCreateJob}
          job={copyFromJob}
        />
      )}

      <SpheraNotificationGroup
        position={{
          top: 70,
          right: 30,
        }}
      >
        {notification.isOpen && (
          <SpheraNotification
            closable={true}
            type={notification.type}
            text={notification.text}
            onClose={() => setNotification(defaultNotification)}
          />
        )}
      </SpheraNotificationGroup>
      {deleteConfirmation && (
        <SpheraConfirmation
          hasAction
          isDangerConfirm
          actionText="Delete"
          onCancel={() => closeConfirmation()}
          onAction={() => removeJob(selectedId)}
          title="Delete this job"
        >
          Once you delete <b>{jobs.find((job) => job.jobId === selectedId)?.name}</b> job, it will be removed forever. Are you sure?
        </SpheraConfirmation>
      )}
      {bulkDeleteConfirmation && (
        <SpheraConfirmation
          hasAction
          isDangerConfirm
          actionText="Delete"
          onCancel={() => setBulkDeleteConfirmation(false)}
          onAction={() => removeSelectedJobs()}
          title="Delete jobs"
        >
          Are you sure you want to delete the selected jobs?
          {renderSelectedJobs()}
        </SpheraConfirmation>
      )}
    </div>
  );
};
